import { afterNextRender, DestroyRef, Directive, ElementRef, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';
import { ScrollPositionStore } from './scroll-position-store.service';

/** How long to keep re-applying the position while async content still loads. */
const RESTORE_TIMEOUT_MS = 3000;

/**
 * Persists and restores the vertical scroll position of its host element,
 * keyed by {@link key}, across component recreations (e.g. router navigation).
 *
 * Angular Router's scroll restoration only handles the window, not inner
 * `overflow-y-auto` containers — this directive covers those. The position is
 * captured once, on `NavigationStart` (while the element is still fully live and
 * scrolled), and re-applied as the (asynchronously loaded) content grows, so it
 * works even when the list is fetched after the container is first rendered.
 */
@Directive({
  selector: '[maiScrollPosition]',
})
export class ScrollPositionDirective {
  /** Stable identifier of this scroll container across navigations. */
  readonly key = input.required<string>({ alias: 'maiScrollPosition' });

  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly store = inject(ScrollPositionStore);
  private readonly router = inject(Router);

  private observer?: MutationObserver;

  constructor() {
    // Save the position when a navigation begins: the element is still attached
    // and scrolled at this point, so scrollTop is reliable (unlike in ngOnDestroy).
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.store.set(this.key(), this.element.scrollTop));

    afterNextRender(() => this.restoreScrollPosition());

    // Disconnect the observer if the view is destroyed mid-restore.
    inject(DestroyRef).onDestroy(() => this.stopObserving());
  }

  private restoreScrollPosition(): void {
    const target = this.store.get(this.key());
    if (target <= 0) return;

    if (this.applyScroll(target)) return;

    // Content is still loading: re-apply as the DOM grows, bounded by a deadline.
    // The observer disconnects as soon as the target is reached, so it only runs
    // during the brief loading window — never continuously.
    const deadline = performance.now() + RESTORE_TIMEOUT_MS;
    this.observer = new MutationObserver(() => {
      if (this.applyScroll(target)) {
        this.stopObserving();
      } else if (performance.now() >= deadline) {
        // Best effort: the list may now be shorter than before.
        this.element.scrollTop = target;
        this.stopObserving();
      }
    });
    this.observer.observe(this.element, { childList: true, subtree: true });
  }

  /** Sets scrollTop to target once the content is tall enough. Returns success. */
  private applyScroll(target: number): boolean {
    const maxScroll = this.element.scrollHeight - this.element.clientHeight;
    if (maxScroll < target) return false;
    this.element.scrollTop = target;
    return true;
  }

  private stopObserving(): void {
    this.observer?.disconnect();
    this.observer = undefined;
  }
}
